/**
 * Reward pops, drawn (world/rewardPopModel.ts has the timeline): every kill and pickup the hero
 * gets credit for pops "+XP" (violet), "+gold" (gold, coin glyph) and gear (rarity colour, a glint
 * for rare and up) out of the world, then each flies into its HUD counter, which pulses as it lands
 * with a small tick sound. One system for fields and dungeons: the world layer calls `add` for
 * field kills and pickups, the in-run HUD routes M+ kill rewards here too.
 *
 * Readable in big pulls: merged tallies, at most MAX_POPS, scaled with the combat-text scale, and
 * dimmed under the LEVEL UP banner (.fx-celebrating).
 */
import { avoidPanels } from '../avoid'; // +polish
import type { HudUpdate } from '../hud';
import type { HudWidget } from '../widgets';
import { el } from '../dom';
import { playSound } from '../../audio/engine';
import { combatTextSettings } from '../combatTextSettings';
import { type RewardKind, type RewardPart, MAX_POPS, RewardPopModel, popPhase, popText, riseShape } from '../../world/rewardPopModel';

/** The live widget (null outside a session); world/worldApp.ts and the in-run HUD feed it. */
export const rewardPops: { current: RewardPops | null } = { current: null };

const RARITY_COLOR: Readonly<Record<string, string>> = { common: '#e8e6df', uncommon: '#6fe07a', rare: '#5aa8ff', epic: '#c77dff', legendary: '#ff9f3a' };
/** Land sounds (rate-limited per kind). */
const LAND_SOUND: Readonly<Record<RewardKind, string>> = { xp: 'reward.xp', gold: 'reward.coin', item: 'reward.item' };
const SOUND_GAP_MS = 70;

interface Slot {
  el: HTMLElement;
  main: HTMLElement;
  tail: HTMLElement;
  id: number;
  /** Screen point where the fly starts (the end of the rise). */
  sx: number;
  sy: number;
}

export class RewardPops implements HudWidget {
  readonly model = new RewardPopModel();
  private readonly layer: HTMLElement;
  private readonly slots: Slot[] = [];
  private readonly lastSound: Record<string, number> = {};
  private scale = 1;
  private readonly unsub: () => void;

  constructor(hudRoot: HTMLElement) {
    this.layer = el('div', 'fx-rpops', hudRoot);
    for (let i = 0; i < MAX_POPS + 2; i++) {
      const e = el('div', 'fx-rpop hk-hidden', this.layer);
      const main = el('span', 'fx-rpop-main hk-num', e);
      const tail = el('span', 'fx-rpop-tail', e);
      this.slots.push({ el: e, main, tail, id: 0, sx: 0, sy: 0 });
    }
    this.unsub = combatTextSettings.subscribe((s) => (this.scale = s.scale));
    rewardPops.current = this;
  }

  /** Rewards from one source at world position `pos` (sim yards). */
  add(pos: { x: number; y: number }, parts: readonly RewardPart[]): void {
    this.model.add(performance.now(), pos, parts);
  }

  update(h: HudUpdate, now: number): void {
    const m = this.model;
    m.prune(now);
    const used = new Set<number>();
    for (const p of m.pops) {
      const { phase, k } = popPhase(p, now);
      let slot = this.slots.find((s) => s.id === p.id);
      if (phase === 'wait' || phase === 'done') {
        if (phase === 'done' && !p.landed) this.land(p.kind, p.rarity, now);
        if (phase === 'done') p.landed = true;
        continue;
      }
      if (!slot) {
        slot = this.slots.find((s) => s.id === 0 || !m.pops.some((q) => q.id === s.id));
        if (!slot) continue;
        slot.id = p.id;
        const t = popText(p);
        slot.main.textContent = t.main;
        slot.tail.textContent = t.tail;
        slot.el.className = `fx-rpop k-${p.kind} r-${p.rarity}${p.count > 3 ? ' big' : ''}`;
        if (p.kind === 'item') slot.el.style.setProperty('--rc', RARITY_COLOR[p.rarity] ?? '#fff');
      } else if (p.count > 1) {
        const t = popText(p);
        if (slot.main.textContent !== t.main) slot.main.textContent = t.main;
        if (slot.tail.textContent !== t.tail) slot.tail.textContent = t.tail;
        slot.el.classList.toggle('big', p.count > 3);
      }
      used.add(slot.id);
      let x: number;
      let y: number;
      let s = this.scale;
      let a = 1;
      if (phase === 'rise' || phase === 'hold') {
        const sp = h.renderer.project({ x: p.wx, y: p.wy }, 2.1);
        const r = riseShape(phase === 'rise' ? k : 1);
        // Kinds sit in a little column: XP on top, gold under it, items under that.
        const row = p.kind === 'xp' ? 0 : p.kind === 'gold' ? 1 : 2;
        // +polish: rise beside the HUD panels, never over them (the fly-in still ends on its counter).
        AV.x = sp.x;
        AV.y = sp.y - r.up + row * 22;
        avoidPanels(AV, 90 * s, 24 * s, now);
        x = AV.x;
        y = AV.y;
        s *= r.scale;
        a = sp.visible ? 1 : 0;
        slot.sx = x;
        slot.sy = y;
      } else {
        const tgt = this.target(p.kind);
        const e = k * k * (3 - 2 * k);
        const kk = k * k;
        x = slot.sx + (tgt.x - slot.sx) * e;
        // A slight lift then a dive into the counter.
        y = slot.sy + (tgt.y - slot.sy) * kk - Math.sin(Math.PI * k) * 40;
        s *= 1 - 0.45 * k;
        a = 1 - 0.4 * kk;
      }
      slot.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%) scale(${s.toFixed(3)})`;
      slot.el.style.opacity = a.toFixed(3);
    }
    for (const sl of this.slots) {
      const live = used.has(sl.id);
      if (!live && sl.id !== 0) sl.id = 0;
      sl.el.classList.toggle('hk-hidden', !live);
    }
  }

  /** Screen centre of the HUD counter a kind flies into. */
  private target(kind: RewardKind): { x: number; y: number } {
    const q = (sel: string): HTMLElement | null => {
      const e = document.querySelector<HTMLElement>(sel);
      return e && e.offsetParent !== null ? e : null;
    };
    const elm = kind === 'xp' ? (q('.fx-xp') ?? q('.hkc-xp')) : kind === 'gold' ? q('.hk-currencies .cur-gold') : (q('.hk-menu > :nth-child(3)') ?? q('.hk-currencies .cur-gold'));
    if (!elm) return { x: window.innerWidth / 2, y: window.innerHeight - 30 };
    const r = elm.getBoundingClientRect();
    return kind === 'xp' ? { x: r.left + r.width * 0.5, y: r.top + r.height * 0.5 } : { x: r.left + Math.min(24, r.width / 2), y: r.top + r.height / 2 };
  }

  /** A pop arrived: pulse its counter, tick. */
  private land(kind: RewardKind, rarity: string, now: number): void {
    const sel = kind === 'xp' ? ['.fx-xp', '.hkc-xp'] : kind === 'gold' ? ['.hk-currencies .cur-gold'] : ['.hk-menu', '.hk-currencies .cur-gold'];
    for (const s of sel) {
      const e = document.querySelector<HTMLElement>(s);
      if (!e) continue;
      e.classList.remove('fx-collect');
      void e.offsetWidth; // restart the pulse
      e.classList.add('fx-collect');
    }
    const id = kind === 'item' && (rarity === 'rare' || rarity === 'epic' || rarity === 'legendary') ? 'reward.rare' : LAND_SOUND[kind];
    if (now - (this.lastSound[id] ?? -1e9) < SOUND_GAP_MS) return;
    this.lastSound[id] = now;
    playSound(id);
  }

  dispose(): void {
    this.unsub();
    this.layer.remove();
    if (rewardPops.current === this) rewardPops.current = null;
  }
}

const AV = { x: 0, y: 0 };
